import { getError } from 'crono-fe-common/utils';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { isCronoError } from './validationUtils';
import { toast } from '@crono-one/design-system/side-effects';
import { getMeta, MutationMeta } from 'crono-fe-common/types/mutationMeta';

export const is403 = (err: unknown) =>
  (err as { response?: { status?: number } })?.response?.status === 403;

// Unlike mutations, query errors are silent by default (background refetches,
// polling and existence checks must not spam toasts). Opt a query in by setting
// `meta.errorMessage` — it doubles as the opt-in flag and the fallback text when
// the backend doesn't provide a readable error.
export type QueryErrorMeta = {
  errorMessage?: string;
  silentError?: boolean;
};

export const queryClient = new QueryClient({
  defaultOptions: {
    // Chrome can report navigator.onLine === false while the network works;
    // the default 'online' mode would then pause every request silently.
    queries: {
      networkMode: 'always',
      retry: (failureCount, err) => !is403(err) && failureCount < 1,
      staleTime: 60 * 1000, // 60s
    },
    mutations: {
      networkMode: 'always',
    },
  },
  queryCache: new QueryCache({
    onError: (err, query) => {
      const meta = (query.meta ?? {}) as QueryErrorMeta;

      if (axios.isCancel(err)) return;
      if (!meta.errorMessage || meta.silentError || is403(err)) return;

      const msg =
        (isCronoError(err) && getError(err)) ||
        (err instanceof Error && err?.message) ||
        meta.errorMessage;
      if (msg)
        toast.error(msg, {
          id: msg,
        });
    },
  }),
  mutationCache: new MutationCache({
    onSuccess: (data, _vars, _ctx, m) => {
      const base = (m.options.meta ?? {}) as MutationMeta;
      const override = getMeta(m.state.variables) ?? {};
      const meta: MutationMeta = { ...base, ...override };

      if (meta.silentAll || meta.silentSuccess) return;

      const msg =
        typeof meta.successMessage === 'function'
          ? meta.successMessage(data, m.state.variables)
          : meta.successMessage;
      if (msg) toast.success(msg);
    },
    onError: (err, _vars, _ctx, m) => {
      const base = (m.options.meta ?? {}) as MutationMeta;
      const override = getMeta(m.state.variables) ?? {};
      const meta: MutationMeta = { ...base, ...override };

      if (axios.isCancel(err)) return;
      if (meta.silentAll || meta.silentError || is403(err)) return;

      const msg =
        typeof meta.errorMessage === 'function'
          ? meta.errorMessage(err, m.state.variables)
          : (isCronoError(err) && getError(err)) ||
            (err instanceof Error && err?.message) ||
            meta.errorMessage ||
            'Something went wrong';
      if (msg)
        toast.error(msg, {
          id: msg,
        });
    },
  }),
});

export function toQueryString(
  params: Record<string, string | number | boolean | null | undefined>,
): string {
  const entries = Object.entries(params).filter(
    (entry): entry is [string, string | number | boolean] => entry[1] != null,
  );

  return new URLSearchParams(
    entries.map(([key, value]) => [key, String(value)]),
  ).toString();
}
